<template>
  <div>
    <div
      :class="[
        'relative',
        isMobileApp && !condensed ? 'pt-4' : '',
        condensed ? 'p-3' : 'p-6 md:p-12',
        haveBackdropImage && showBackdropImage
          ? 'text-white'
          : !haveBackdropImage
            ? ''
            : !showBackdropImage
              ? 'text-gray-800'
              : 'text-white',
        dark ? 'text-white' : 'text-gray-800'
      ]"
      :style="
        showBackdropImage
          ? {
              backgroundColor: dominantColor
                ? `rgb(${dominantColor.join(',')})`
                : '#000'
            }
          : null
      ">
      <!-- Background Image Container -->
      <template v-if="showBackdropImage">
        <div
          class="absolute inset-0 z-10 bg-gray-800 bg-center bg-cover opacity-100"></div>

        <div
          class="absolute inset-0 z-10 bg-center bg-cover opacity-20"
          :style="{
            backgroundImage: backdropImage
              ? `url(https://image.tmdb.org/t/p/w1280${backdropImage})`
              : ''
          }"></div
      ></template>
      <template v-else-if="!condensed">
        <div
          class="absolute inset-0 z-10 bg-gray-800 bg-center bg-cover opacity-100"></div>
      </template>

      <!-- Title Info Container -->
      <div
        class="flex flex-row items-center gap-2 md:gap-4"
        :class="
          condensed
            ? 'pt-0'
            : isNativeApp
              ? 'pt-24'
              : isMobileApp
                ? 'pt-12'
                : 'pt-0'
        ">
        <!-- Poster Image -->
        <div
          :class="[
            'relative z-10 block rounded-md shadow-md group',
            condensed ? 'w-12' : 'w-24 md:w-36 xl:w-60'
          ]">
          <TitlePosterCard
            v-if="title"
            :title="title"
            :title-type="titleType"
            :class="condensed ? 'w-12' : 'w-24 md:w-36 xl:w-60'"
            :lightbox="true"
            :controls="condensed ? false : true" />
          <TitlePosterCardSkeleton
            v-else
            :class="condensed ? 'w-12' : 'w-24 md:w-36 xl:w-60'" />
        </div>

        <!-- Title Information -->
        <div class="relative z-10 flex flex-col justify-center gap-y-1 min-w-0">
          <router-link
            v-if="haveTitle"
            :to="{
              name: 'WebTitle',
              params: { id: title.id, type: effectiveTitleType }
            }"
            :class="[
              'font-semibold leading-none tracking-tight cursor-pointer hover:underline',
              condensed
                ? 'truncate'
                : 'md:text-xl lg:text-2xl text-pretty'
            ]">
            {{ displayTitle }}
          </router-link>

          <div
            class="flex items-center divide-x"
            :class="[
              showBackdropImage
                ? 'text-gray-100 divide-gray-100'
                : dark
                  ? 'text-white divide-white'
                  : 'text-gray-600 divide-gray-400'
            ]">
            <template v-if="haveTitle">
              <div
                class="px-1.5 font-medium truncate text-2xs sm:text-xs first:pl-0 last:pr-0">
                {{ releaseYear }}
              </div>

              <!-- <div
            class="px-1.5 text-xs font-medium"
            :class="title.media_type === 'tv' ? 'uppercase' : 'capitalize'">
            {{ title.media_type }}
          </div> -->

              <div
                v-if="originCountry"
                class="px-1.5 font-medium capitalize text-2xs sm:text-xs">
                {{ originCountry }}
              </div>
              <div
                v-if="
                  haveTitle &&
                  titleType === 'tv' &&
                  title?.number_of_seasons !== undefined
                "
                class="px-1.5 font-medium truncate text-2xs sm:text-xs">
                {{ title.number_of_seasons }}
                {{ title.number_of_seasons === 1 ? 'Season' : 'Seasons' }}
              </div>
              <div
                v-if="
                  haveTitle &&
                  titleType === 'movie' &&
                  title?.runtime &&
                  title.runtime > 0
                "
                class="px-1.5 font-medium truncate text-2xs sm:text-xs">
                {{ title.runtime + ' mins' }}
              </div>
              <div
                v-if="certification && certification !== 'N/A'"
                class="mr-2 font-medium truncate text-2xs sm:text-xs">
                <span
                  class="px-1 ml-2 border rounded"
                  :class="
                    showBackdropImage ? 'border-white' : 'border-gray-600'
                  "
                  >{{ certification }}</span
                >
              </div>
            </template>
          </div>

          <div
            v-if="showNotes"
            class="p-1 space-y-2 text-xs bg-gray-100 text-muted-foreground">
            <div
              class="inline-block px-1 py-0.5 font-bold bg-gray-300 rounded cursor-pointer hover:bg-blue-800 hover:text-white">
              <fa-icon icon="notes" class="mr-1" /> +Note
            </div>
            <div>
              <fa-icon icon="notes" class="mr-1 text-gray-600" /> Lorem ipsum
              dolor sit amet consectetur adipisicing elit. Assumenda pariatur,
              quis reprehenderit, explicabo delectus iste laboriosam veniam,
              facere perspiciatis necessitatibus porro. Harum alias consequuntur
              nesciunt quis culpa, esse distinctio ipsa.
            </div>
          </div>

          <div v-if="!condensed">
            <!-- Director Link -->
            <p
              v-if="directorName"
              class="text-xs font-semibold text-gray-100 md:text-sm">
              Director:
              <router-link
                :to="{
                  name: 'WebTitle',
                  params: { id: directorName.id, type: 'person' }
                }"
                class="text-white hover:underline">
                {{ directorName.name }}
              </router-link>
            </p>

            <!-- Trailer Link -->
            <div v-if="trailerUrl">
              <a
                :href="trailerUrl"
                target="_blank"
                class="inline-block text-xs font-semibold text-white md:text-sm">
                <fa-icon :icon="['fab', 'youtube']" class="mr-1" />
                Watch Trailer
              </a>
            </div>

            <!-- Top Actors -->
            <div v-if="topActorsList">
              <p class="text-xs text-gray-100 md:font-semibold md:text-sm">
                Starring:
                <span v-for="(actor, index) in topActorsList" :key="actor.id">
                  <router-link
                    :to="{
                      name: 'WebTitle',
                      params: { id: actor.id, type: 'person' }
                    }"
                    class="text-white hover:underline">
                    {{ actor.name }}
                  </router-link>
                  <span v-if="index < topActorsList.length - 1">,&nbsp;</span>
                </span>
              </p>
            </div>

          </div>

          <!-- Compact User Activity (icons only) -->
          <div
            v-if="condensed && hasUserActivityCompact"
            class="flex items-center gap-1 text-2xs">
            <fa-icon
              v-if="isSeen"
              :icon="['fas', 'hexagon-check']"
              class="text-green-400" />
            <fa-icon
              v-else-if="isInProgress"
              :icon="['fas', 'play-circle']"
              class="text-cyan-400" />
            <fa-icon
              v-else-if="isWatchlisted"
              :icon="['fas', 'bookmark']"
              class="text-amber-400" />
            <fa-icon
              v-if="isFaved"
              :icon="['fas', 'heart']"
              class="text-red-400" />
            <router-link
              v-if="userReview"
              :to="{ name: 'WebReviewsSingle', params: { id: userReview.id } }"
              class="flex items-center gap-1">
              <fa-icon
                :icon="['fas', 'hundred-points']"
                class="text-yellow-400" />
              <span
                v-if="userReview.score_overall != null"
                :class="scoreColor"
                class="font-bold"
                >{{ formattedScore }}</span
              >
            </router-link>
          </div>
        </div>
      </div>

      <!-- Quick Actions Row (full width, below poster/info) -->
      <TitleActionsBlock
        v-if="title && !condensed"
        :title="title"
        :title-info="titleInfo"
        class="relative z-10 mt-4" />
    </div>

    <!-- Tiny Title Strip -->
    <!-- 
      v-show="isScrolled" -->
    <div
      v-if="false"
      class="fixed top-0 left-0 z-50 flex items-center w-full gap-4 px-4 py-1 text-white bg-gray-800 shadow-md">
      <!-- Poster Image -->
      <div class="w-10 overflow-hidden rounded-sm shadow-md h-14">
        <img
          :src="posterImage"
          alt="Poster"
          class="object-cover w-full h-full" />
      </div>

      <!-- Title Information -->
      <div class="flex flex-col gap-0.5">
        <h3 class="text-xs font-semibold truncate">
          {{ displayTitle }} ({{ releaseYear }})
        </h3>
        <div class="flex items-center text-gray-100 divide-x divide-gray-100">
          <div
            v-if="certification"
            class="px-1.5 mr-2 font-medium rounded-sm border text-2xs">
            {{ certification }}
          </div>

          <div
            class="px-1.5 font-medium capitalize text-2xs first:pl-0 last:pr-0">
            {{ originCountry }}
          </div>

          <div v-if="titleType === 'tv'" class="px-1.5 text-xs font-medium">
            {{ title.number_of_seasons }}
            {{ title.number_of_seasons === 1 ? 'Season' : 'Seasons' }}
          </div>

          <div class="px-1.5 font-medium text-2xs">
            {{ titleType === 'movie' ? title.runtime + ' mins' : title.status }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, inject, watch } from 'vue'
import TitlePosterCard from '@/components/TitlePosterCard'
import TitlePosterCardSkeleton from '@/components/TitlePosterCardSkeleton'
import TitleActionsBlock from '@/components/TitleActionsBlock.vue'
import { useTitleInfo } from '@/use/useTitleInfo'
import { useStoreReviews } from '@/stores/storeReviews'
import { useStoreLists } from '@/stores/storeLists'
import { useStoreAuth } from '@/stores/storeAuth'
import { useStoreTitlesUser } from '@/stores/storeTitlesUser'
import { useStoreTitleLogs } from '@/stores/storeTitleLogs'
import { useStoreProfile } from '@/stores/storeProfile'
import { format } from 'date-fns'
import ColorThief from 'colorthief'

// Get platform information
const { isMobileApp, isNativeApp } = inject('platform')

// Stores for user activity
const storeReviews = useStoreReviews()
const storeLists = useStoreLists()
const storeAuth = useStoreAuth()
const storeTitlesUser = useStoreTitlesUser()
const storeTitleLogs = useStoreTitleLogs()
const storeProfile = useStoreProfile()

// Log system is always enabled
const usesLogSystem = computed(() => true)

const props = defineProps({
  title: Object,
  titleInfo: Object,
  titleType: String,
  userTitleInfo: Object,
  condensed: {
    type: Boolean,
    default: false // Default to false for the normal style
  },
  dark: {
    type: Boolean,
    default: false
  },
  showNotes: {
    type: Boolean,
    default: false
  }
})

const haveTitle = computed(
  () =>
    props.title !== null &&
    props.title !== undefined &&
    !!(props.titleType || props.title?.media_type)
)
const titleComputed = computed(() => props?.title || props?.titleInfo)

// Safely destructure values from useTitleInfo with fallbacks
const titleInfoValues = useTitleInfo(titleComputed)
const releaseYear = computed(() => titleInfoValues.releaseYear.value)
const displayTitle = computed(() => titleInfoValues.displayTitle.value)
const certification = computed(() => titleInfoValues.certification.value)
const originCountry = computed(() => titleInfoValues.originCountry.value)

// Get the director's name from crew credits and make it a link
const directorName = computed(() => {
  if (!haveTitle.value || !props.title) return null
  if (props.title?.credits && Array.isArray(props.title.credits.crew)) {
    const director = props.title.credits.crew.find(
      (member) => member.job === 'Director'
    )
    return director ? { name: director.name, id: director.id } : null
  }
  return null
})

// Get the first backdrop image
const backdropImage = computed(() => {
  if (!haveTitle.value || !props.title) return null
  if (props.title?.images && Array.isArray(props.title?.images.backdrops)) {
    return props.title.images.backdrops.length > 0
      ? props.title.images.backdrops[0].file_path
      : null
  }
  return null
})

// Get the trailer URL
const trailerUrl = computed(() => {
  if (!haveTitle.value || !props.title) return null
  if (props.title?.videos && Array.isArray(props.title.videos.results)) {
    const trailer = props.title.videos.results.find(
      (video) => video.type === 'Trailer' && video.site === 'YouTube'
    )
    return trailer ? `https://www.youtube.com/watch?v=${trailer.key}` : null
  }
  return null
})

// Get the top three actors as links
const topActorsList = computed(() => {
  if (!haveTitle.value || !props.title) return null
  if (props.title?.credits && Array.isArray(props.title.credits.cast)) {
    const actors = props.title.credits.cast.slice(0, 3).map((actor) => ({
      name: actor.name,
      id: actor.id
    }))
    return actors.length > 0 ? actors : null
  }
  return null
})

// Get the poster image URL
const posterImage = computed(() => {
  if (!haveTitle.value || !props.title) return null
  return props.title?.poster_path
    ? `https://image.tmdb.org/t/p/w500${props.title.poster_path}`
    : null
})

// To hold the dominant color
const dominantColor = ref(null)

// Track scrolling
// const isScrolled = ref(false)

// const handleScroll = () => {
//   isScrolled.value = window.scrollY > 200 // Adjust threshold as needed
// }

onMounted(() => {
  getDominantColor()
  // window.addEventListener('scroll', handleScroll)
  // Fetch user data if not passed as prop
  fetchUserData()
})

// Fetch user title info and list items from store if not passed as prop
const fetchUserData = () => {
  const titleType = props.titleType || props.title?.media_type
  if (props.title?.id && titleType) {
    // Fetch user title info
    if (!props.userTitleInfo) {
      storeTitlesUser.fetchInfoByTitle({
        id: props.title.id,
        type: titleType
      })
    }
    // Fetch list items for this title
    storeLists.fetchItemsByTitle({
      id: props.title.id,
      type: titleType
    })
  }
}

// Watch for title changes to fetch user info
watch(
  () => props.title?.id,
  () => {
    fetchUserData()
  }
)

onUnmounted(() => {
  // window.removeEventListener('scroll', handleScroll)
})

const getDominantColor = () => {
  if (posterImage.value) {
    const img = new Image()
    img.crossOrigin = 'Anonymous'

    img.onload = () => {
      try {
        const colorThief = new ColorThief()
        dominantColor.value = colorThief.getColor(img)
      } catch (error) {
        console.warn('Failed to extract dominant color:', error)
        // Fallback to a default color if extraction fails
        dominantColor.value = [100, 100, 100]
      }
    }

    img.onerror = () => {
      console.warn(
        'Failed to load image for color extraction:',
        posterImage.value
      )
      // Fallback to a default color if image fails to load
      dominantColor.value = [100, 100, 100]
    }

    // Set src after setting up crossOrigin and event handlers
    img.src = posterImage.value
  }
}

const showBackdropImage = computed(() => {
  return !props.condensed && backdropImage.value
})

const haveBackdropImage = computed(() => !!backdropImage.value)

// User activity computeds
const currentUserId = computed(() => storeAuth.user?.id)
const isLoggedIn = computed(() => !!currentUserId.value)

// Derive titleType from props or from title.media_type
const effectiveTitleType = computed(() => {
  return props.titleType || props.title?.media_type || null
})

// Look up userTitleInfo from store if not passed as prop
const effectiveUserTitleInfo = computed(() => {
  if (props.userTitleInfo) return props.userTitleInfo
  if (!props.title?.id || !effectiveTitleType.value) return null
  return storeTitlesUser.getTitleByTitleId({
    id: props.title.id,
    type: effectiveTitleType.value
  })
})

const isSeen = computed(() => {
  // Log system is authoritative when enabled — don't fall through to legacy
  if (usesLogSystem.value && props.title?.id) {
    return (
      storeTitleLogs.getDerivedStateByTmdb(
        props.title.id,
        effectiveTitleType.value
      ) === 'seen'
    )
  }
  // Legacy system
  if (!effectiveUserTitleInfo.value) return false
  return !!effectiveUserTitleInfo.value.is_seen
})

// Check if in progress (log system only)
const isInProgress = computed(() => {
  if (!usesLogSystem.value || !props.title?.id) return false
  const logState = storeTitleLogs.getDerivedStateByTmdb(
    props.title.id,
    effectiveTitleType.value
  )
  return logState === 'in_progress'
})

// Check if watchlisted
const isWatchlisted = computed(() => {
  // Log system is authoritative when enabled — don't fall through to legacy
  if (usesLogSystem.value && props.title?.id) {
    return (
      storeTitleLogs.getDerivedStateByTmdb(
        props.title.id,
        effectiveTitleType.value
      ) === 'watchlist'
    )
  }
  // Legacy system
  if (!effectiveUserTitleInfo.value) return false
  return !!effectiveUserTitleInfo.value.is_watchlisted
})

const isFaved = computed(() => {
  if (!effectiveUserTitleInfo.value) return false
  return !!effectiveUserTitleInfo.value.is_favourited
})

const seenDate = computed(() => {
  // Try log system first
  if (usesLogSystem.value && props.title?.id) {
    const log = storeTitleLogs.getLatestLogByTmdb(
      props.title.id,
      effectiveTitleType.value
    )
    if (log?.state === 'seen' && log.watched_on) {
      try {
        return format(new Date(log.watched_on), 'MMM d, yyyy')
      } catch (e) {
        // Fall through to legacy
      }
    }
  }
  // Fall back to legacy
  if (!effectiveUserTitleInfo.value?.seen_at) return null
  try {
    return format(new Date(effectiveUserTitleInfo.value.seen_at), 'MMM d, yyyy')
  } catch (e) {
    console.warn('Failed to format seen date:', e)
    return null
  }
})

const userReview = computed(() => {
  if (!currentUserId.value || !props.title?.id) return null
  const userId = currentUserId.value
  const tmdbId = props.title.id
  if (!storeReviews.reviews || !Array.isArray(storeReviews.reviews)) return null
  const reviews = storeReviews.reviews.filter(
    (review) =>
      review &&
      review.user_id === userId &&
      (review.tmdb_id == tmdbId || review.titles?.tmdb_id == tmdbId)
  )
  return reviews.length > 0 ? reviews[0] : null
})

const userLists = computed(() => {
  if (!currentUserId.value || !props.title?.id || !effectiveTitleType.value)
    return []
  const userId = currentUserId.value
  const tmdbId = props.title.id
  const tmdbType = effectiveTitleType.value
  if (!storeLists.items || !Array.isArray(storeLists.items)) return []

  // Get list items that match this title (use loose equality for type coercion)
  const matchingItems = storeLists.items.filter(
    (item) =>
      item?.titles?.tmdb_id == tmdbId && item?.titles?.tmdb_type == tmdbType
  )

  // Look up list details and filter to user's non-system lists
  return (
    matchingItems
      .map((item) => {
        // Use loose equality for list_id comparison
        const list = storeLists.lists.find((l) => l.id == item.list_id)
        return list ? { id: list.id, title: list.title, list } : null
      })
      .filter(
        (item) =>
          item !== null && item.list?.user_id == userId && !item.list?.is_system
      )
      // Remove duplicates by list id
      .filter(
        (item, index, self) => self.findIndex((l) => l.id === item.id) === index
      )
      .map(({ id, title }) => ({ id, title: title || 'Untitled List' }))
  )
})

const formattedScore = computed(() => {
  const rawScore = Number(userReview.value?.score_overall ?? 0)
  return rawScore.toFixed(1)
})

const scoreColor = computed(() => {
  const score = userReview.value?.score_overall
  if (score == null) return 'text-gray-400'
  if (score >= 8) return 'text-green-400'
  if (score >= 6) return 'text-yellow-400'
  if (score >= 4) return 'text-orange-400'
  return 'text-red-400'
})

const hasUserActivity = computed(() => {
  if (!isLoggedIn.value || props.condensed) return false
  return (
    isSeen.value ||
    isInProgress.value ||
    isWatchlisted.value ||
    isFaved.value ||
    userReview.value
  )
})

const hasUserActivityCompact = computed(() => {
  if (!isLoggedIn.value) return false
  return (
    isSeen.value ||
    isInProgress.value ||
    isWatchlisted.value ||
    isFaved.value ||
    userReview.value
  )
})
</script>
